import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { bannerMessage, fetchTwoFaStatus, onTwoFaChanged, promptFor, URGENT_DAYS, type TwoFaStatus } from '../../lib/twoFa';

const DISMISS_KEY = 'sk_2fa_banner_dismissed';
// Pages that stay open whatever the requirement says: sign-in, legal, the setup itself.
const OPEN_PAGES = ['/login', '/register', '/forgot-password', '/reset-password', '/auth', '/legal', '/security'];
const isOpenPage = (pathname: string) => OPEN_PAGES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
const STALE_MS = 60_000;

/**
 * When two-factor authentication is required of the signed-in person and they
 * have not set it up: a notice with the days left, and after the deadline only
 * the setup screen. Renders nothing for everyone else.
 */
export function TwoFactorRequirement() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState<TwoFaStatus | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const fetchedAt = useRef(0);

  const refresh = useCallback(async () => {
    fetchedAt.current = Date.now();
    setStatus(await fetchTwoFaStatus());
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { setStatus(null); fetchedAt.current = 0; return; }
    if (Date.now() - fetchedAt.current > STALE_MS) void refresh();
  }, [user, loading, pathname, refresh]);

  useEffect(() => onTwoFaChanged(() => { void refresh(); }), [refresh]);

  const prompt = promptFor(user ? status : null);

  useEffect(() => {
    if (prompt === 'blocked' && !isOpenPage(pathname)) navigate('/security/setup', { replace: true });
  }, [prompt, pathname, navigate]);

  if (prompt !== 'banner' || !status || isOpenPage(pathname)) return null;
  const urgent = (status.days_left ?? 0) <= URGENT_DAYS;
  if (dismissed && !urgent) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-2 z-[70] mx-auto max-w-md rounded-xl border border-gold/40 bg-app-card shadow-lg px-4 py-2.5 flex items-center gap-3"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
    >
      <p className="flex-1 text-sm text-app-fg">{bannerMessage(status)}</p>
      <Link to="/security/setup" className="text-sm font-semibold text-gold whitespace-nowrap sk-hit">Set up</Link>
      {!urgent && (
        <button
          aria-label="Dismiss"
          className="text-app-muted hover:text-app-fg text-lg leading-none sk-hit"
          onClick={() => { setDismissed(true); try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ } }}
        >
          ×
        </button>
      )}
    </div>
  );
}
